import { getWindow } from "@faire/web--source/common/globals/getWindow";
import * as React from "react";
import { UserPerceivedLoadTiming } from "./UserPerceivedLoadTiming";
import { useTrackCumulativeLayoutShift, useTrackFirstContentfulPaint, useTrackInteractionToNextPaint, useTrackLargestContentfulPaint } from "./useTrackWebVitals";
export var DEPRECATED_RESTART_WHEN_ISPAGELOADING_BECOMES_TRUE = {
    __unique_obj: "DEPRECATED_RESTART_WHEN_ISPAGELOADING_BECOMES_TRUE"
};
export var useUserPerceivedLoadTimingWithKey = function(options) {
    var _recorderRef_current;
    var pageMountTimeRef = React.useRef(undefined);
    var recorderRef = React.useRef(undefined);
    var enabled = options.enabled, pageLoadKey = options.pageLoadKey, isPageLoading = options.isPageLoading;
    var didKeyChange = useDidValueChange(pageLoadKey);
    var didIsPageLoadingChange = useDidValueChange(isPageLoading);
    var loadType = !isPageLoading && recorderRef.current ? recorderRef === null || recorderRef === void 0 ? void 0 : (_recorderRef_current = recorderRef.current) === null || _recorderRef_current === void 0 ? void 0 : _recorderRef_current.loadType : undefined;
    useTrackCumulativeLayoutShift(options.name, loadType);
    useTrackInteractionToNextPaint(options.name, loadType);
    useTrackFirstContentfulPaint(options.name, !!options.actionStartTime);
    useTrackLargestContentfulPaint(options.name, !!options.actionStartTime);
    if (enabled !== false) {
        var shouldRestart = pageLoadKey === DEPRECATED_RESTART_WHEN_ISPAGELOADING_BECOMES_TRUE ? didIsPageLoadingChange && isPageLoading : didKeyChange;
        if (shouldRestart) {
            var _recorderRef_current1;
            (_recorderRef_current1 = recorderRef.current) === null || _recorderRef_current1 === void 0 ? void 0 : _recorderRef_current1.cancel();
            recorderRef.current = new UserPerceivedLoadTiming(options, {
                pageMountTime: pageMountTimeRef.current,
                dataUpdatedAt: options.dataUpdatedAt,
                pageJsExecutedMark: options.pageJsExecutedMark
            });
        }
    }
    React.useLayoutEffect(function() {
        var _getWindow, _recorderRef_current;
        pageMountTimeRef.current = (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : _getWindow.performance.now();
        (_recorderRef_current = recorderRef.current) === null || _recorderRef_current === void 0 ? void 0 : _recorderRef_current.notifyPageMount();
        return function() {
            var _recorderRef_current;
            (_recorderRef_current = recorderRef.current) === null || _recorderRef_current === void 0 ? void 0 : _recorderRef_current.notifyPageUnmount();
        };
    }, []);
    React.useLayoutEffect(function() {
        if (pageLoadKey !== DEPRECATED_RESTART_WHEN_ISPAGELOADING_BECOMES_TRUE) {
            var _recorderRef_current;
            (_recorderRef_current = recorderRef.current) === null || _recorderRef_current === void 0 ? void 0 : _recorderRef_current.notifyReadyToFetchData();
        }
    }, [
        pageLoadKey
    ]);
    React.useLayoutEffect(function() {
        if (pageLoadKey === DEPRECATED_RESTART_WHEN_ISPAGELOADING_BECOMES_TRUE && isPageLoading) {
            var _recorderRef_current;
            (_recorderRef_current = recorderRef.current) === null || _recorderRef_current === void 0 ? void 0 : _recorderRef_current.notifyReadyToFetchData();
        }
    }, [
        pageLoadKey,
        isPageLoading
    ]);
    React.useLayoutEffect(function() {
        if (!isPageLoading) {
            var _recorderRef_current;
            (_recorderRef_current = recorderRef.current) === null || _recorderRef_current === void 0 ? void 0 : _recorderRef_current.notifyRenderedWithData();
        }
    }, [
        pageLoadKey,
        isPageLoading
    ]);
    React.useEffect(function() {
        var _recorderRef_current;
        (_recorderRef_current = recorderRef.current) === null || _recorderRef_current === void 0 ? void 0 : _recorderRef_current.setDataUpdatedAt(options.dataUpdatedAt);
    }, [
        options.dataUpdatedAt
    ]);
    React.useEffect(function() {
        var _recorderRef_current;
        (_recorderRef_current = recorderRef.current) === null || _recorderRef_current === void 0 ? void 0 : _recorderRef_current.setPageJsExecutedMark(options.pageJsExecutedMark);
    }, [
        options.pageJsExecutedMark
    ]);
    React.useEffect(function() {
        var _recorderRef_current;
        var _options_ignoreImages;
        (_recorderRef_current = recorderRef.current) === null || _recorderRef_current === void 0 ? void 0 : _recorderRef_current.setIgnoreImages((_options_ignoreImages = options.ignoreImages) !== null && _options_ignoreImages !== void 0 ? _options_ignoreImages : false);
    }, [
        options.ignoreImages
    ]);
    React.useEffect(function() {
        var _recorderRef_current;
        (_recorderRef_current = recorderRef.current) === null || _recorderRef_current === void 0 ? void 0 : _recorderRef_current.setNumComponents(options.numComponents);
    }, [
        options.numComponents
    ]);
    React.useEffect(function() {
        /* This useEffect handles two scenarios when a UPLT measurement should be started:
         * - `isPageLoading` changes from `true` to `false` (ie. fetch with cache miss).
         * - `pageLoadKey` changes and `isPageLoading` is `false` (ie. useQuery cache hit).
         */ if (!isPageLoading && recorderRef.current) {
            recorderRef.current.start();
        }
    }, [
        pageLoadKey,
        isPageLoading
    ]);
    if (isPageLoading) {
        /* In the scenario where `isPageLoading === true` we use a no-op ref callback.
         * This prevents `refCallback` from being passed `null` if the target
         * component unmounts for the loading state, which would cancel the
         * measurement with status TARGET_COMPONENT_UNMOUNTED.
         */ return emptyRefCallback;
    } else if (recorderRef.current) {
        /* Wait until `isPageLoading === false` to return the real `refCallback`.
         * When the function identity changes, React will call the new function with each of
         * the already-mounted elements.
         */ return recorderRef.current.refCallback;
    } else {
        /* In the case that `enabled === false` and there is no UPLT instance,
         * return the no-op ref callback.
         */ return emptyRefCallback;
    }
};
function useDidValueChange(value) {
    var previousValueRef = React.useRef(undefined);
    var didChange = previousValueRef.current !== value;
    previousValueRef.current = value;
    return didChange;
}
function emptyRefCallback() {}
